/**
 * usePartnerData Hook
 * 협력사 데이터 관리 훅
 */
import { useState, useEffect, useCallback } from 'react';
import { partnerAccountingApi } from '../api/partnerAccountingApi';
import { PartnerListItem, PartnerNameItem, PartnerSearchFilters } from '../types/partnerAccounting.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';

export const usePartnerData = () => {
    const { showToast } = useToast();

    const savedFilters = loadFilters<{
        searchPartnerId: string;
        searchPartnerIdInput: string;
        searchStatus: string;
        searchWithdrawalStatus: string;
    }>('partnerAccountingFilters');

    const [searchPartnerId, setSearchPartnerId] = useState(savedFilters?.searchPartnerId || '');
    const [searchPartnerIdInput, setSearchPartnerIdInput] = useState(savedFilters?.searchPartnerIdInput || '');
    const [searchStatus, setSearchStatus] = useState(savedFilters?.searchStatus || '');
    const [searchWithdrawalStatus, setSearchWithdrawalStatus] = useState(savedFilters?.searchWithdrawalStatus || '');
    const [allData, setAllData] = useState<PartnerListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [partnerNames, setPartnerNames] = useState<PartnerNameItem[]>([]);

    const fetchPartnerList = useCallback(async () => {
        try {
            setLoading(true);
            const response = await partnerAccountingApi.getPartnerList();
            setAllData(Array.isArray(response) ? response : []);
        } catch {
            showToast('협력사 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setAllData([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    const fetchPartnerNames = useCallback(async () => {
        try {
            const names = await partnerAccountingApi.getPartnerNames();
            setPartnerNames(names);
        } catch {
            setPartnerNames([]);
        }
    }, []);

    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);

            const searchFilters: PartnerSearchFilters = {};
            if (searchPartnerIdInput?.trim()) {
                searchFilters.suppId = searchPartnerIdInput.trim();
            } else if (searchPartnerId?.trim()) {
                searchFilters.suppId = searchPartnerId.trim();
            }
            if (searchStatus?.trim()) {
                searchFilters.statusCode = searchStatus.trim();
            }

            const params: Record<string, string> = {};
            if (searchFilters.suppId) params.suppId = searchFilters.suppId;
            if (searchFilters.statusCode) params.statusCode = searchFilters.statusCode;
            if (searchWithdrawalStatus?.trim()) params.withdrawalYn = searchWithdrawalStatus.trim();

            const response = await partnerAccountingApi.searchPartners(params);
            setAllData(Array.isArray(response) ? response : []);

            saveFilters('partnerAccountingFilters', {
                searchPartnerId,
                searchPartnerIdInput,
                searchStatus,
                searchWithdrawalStatus,
            });
        } catch {
            showToast('협력사 검색에 실패했습니다.', { type: ToastType.ERROR });
            setAllData([]);
        } finally {
            setLoading(false);
        }
    }, [searchPartnerIdInput, searchPartnerId, searchStatus, searchWithdrawalStatus, showToast]);

    const executeWithdrawalLimit = useCallback(async (
        suppId: string,
        newStatus: 'Y' | 'N',
        partnerName: string,
        action: string
    ) => {
        try {
            setLoading(true);
            await partnerAccountingApi.updateWithdrawalStatus(suppId, newStatus);
            showToast(`${partnerName} ${action}이 적용되었습니다.`, { type: ToastType.SUCCESS });
            await fetchPartnerList();
        } catch {
            showToast(`${action} 설정에 실패했습니다.`, { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [fetchPartnerList, showToast]);

    const resetSearchConditions = useCallback(() => {
        setSearchPartnerId('');
        setSearchPartnerIdInput('');
        setSearchStatus('');
        setSearchWithdrawalStatus('');
    }, []);

    useEffect(() => {
        const saved = loadFilters<{
            searchPartnerId: string;
            searchPartnerIdInput: string;
            searchStatus: string;
            searchWithdrawalStatus: string;
        }>('partnerAccountingFilters');

        if (saved && (saved.searchPartnerId || saved.searchPartnerIdInput || saved.searchStatus || saved.searchWithdrawalStatus)) {
            handleSearch();
        } else {
            fetchPartnerList();
        }
        fetchPartnerNames();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        searchPartnerId,
        setSearchPartnerId,
        searchPartnerIdInput,
        setSearchPartnerIdInput,
        searchStatus,
        setSearchStatus,
        searchWithdrawalStatus,
        setSearchWithdrawalStatus,
        allData,
        loading,
        partnerNames,
        handleSearch,
        executeWithdrawalLimit,
        fetchPartnerList,
        resetSearchConditions,
    };
};
